/**
 * The bearer token hand-off of spec 9.7 step 1. The Supabase client lives in the main
 * thread (workers have no localStorage) with autoRefreshToken off; before a sync the
 * worker posts `{ type: 'auth:token', id }`, the main thread refreshes only when online,
 * and answers `{ type: 'auth:token', id, token }` with the access token for this run or
 * null (offline, logged out). The worker never holds the Supabase client.
 *
 * Both halves are here, platform-neutral: `createTokenRequester` runs in the worker,
 * `createTokenResponder` in the main thread (section 05 supplies the provider).
 */
import type { TokenResponseMessage } from '../db/protocol'

export interface TokenRequestMessage {
  type: 'auth:token'
  id: number
}

export type TokenProvider = () => Promise<string | null>

export interface TokenRequesterOptions {
  /** A main thread that never answers must not hang the sync run. */
  timeoutMs?: number
  timers?: {
    setTimeout(fn: () => void, ms: number): unknown
    clearTimeout(handle: unknown): void
  }
}

export interface TokenRequester {
  /** Ask once; resolves null on timeout so the run is treated as offline. */
  request(): Promise<string | null>
  /** Feed an answer; true when it matched an open request. */
  resolve(message: TokenResponseMessage): boolean
  readonly pending: number
}

export const TOKEN_TIMEOUT_MS = 15_000

export function createTokenRequester(
  post: (message: TokenRequestMessage) => void,
  options: TokenRequesterOptions = {},
): TokenRequester {
  const timers = options.timers ?? {
    setTimeout: (fn, ms) => setTimeout(fn, ms),
    clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
  }
  const timeoutMs = options.timeoutMs ?? TOKEN_TIMEOUT_MS
  const open = new Map<number, (token: string | null) => void>()
  let seq = 0
  return {
    request() {
      return new Promise<string | null>((resolve) => {
        const id = ++seq
        const timer = timers.setTimeout(() => {
          if (open.delete(id)) resolve(null)
        }, timeoutMs)
        open.set(id, (token) => {
          timers.clearTimeout(timer)
          resolve(token)
        })
        post({ type: 'auth:token', id })
      })
    },
    resolve(message) {
      const settle = open.get(message.id)
      if (settle === undefined) return false
      open.delete(message.id)
      settle(message.token)
      return true
    },
    get pending() {
      return open.size
    },
  }
}

export interface TokenResponder {
  /** Answer one request; a throwing or missing provider answers null. */
  handle(message: TokenRequestMessage, post: (message: TokenResponseMessage) => void): Promise<void>
}

export function createTokenResponder(getProvider: () => TokenProvider | null): TokenResponder {
  return {
    async handle(message, post) {
      let token: string | null = null
      const provider = getProvider()
      if (provider !== null) {
        try {
          token = (await provider()) ?? null
        } catch {
          token = null
        }
      }
      post({ type: 'auth:token', id: message.id, token })
    },
  }
}
